import React, {
  useRef,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  Dispatch,
  SetStateAction,
  ForwardRefRenderFunction,
} from "react";
import classnames from "classnames";
import { Header, Wrapper } from "@/components";
import { i18n } from "@/utils";
import { TaskStatus } from ".";
import { ImageSort, UserImage, Color } from "./mini-me";
import Icon from "./icon";
import ChooseOption from "./choose-option";
import styles from "./style.less";

export interface ChooseHandle {
  /** 强制渲染 */
  update: () => void;
}

interface ChooseProps {
  /** 用户头像 */
  userImage: UserImage;
  /** 分类选项 */
  sortOption: { [nameId: string]: ImageSort[] };
  /** 带 path 的图片 */
  pathImage: { [propName: number]: string };
  /** 肤色 */
  colors: Color[];
  /** 当前选中的肤色 */
  selectedColor: Color;
  /** 生成 path 图片 */
  createPathImage: () => void;
  /** 重置列表选项 loading 状态 */
  resetOptionLoading: () => void;
  /** 设置当前选中的肤色 */
  setSelectedColor: Dispatch<SetStateAction<Color>>;
  /** 设置已选择选项 */
  setSelectedOption: Dispatch<SetStateAction<ImageSort | undefined>>;
  /** 设置任务状态 */
  setTaskStatus: Dispatch<SetStateAction<TaskStatus>>;
  /** 设置 zuploader组件 显影 */
  setZuploaderVisible: Dispatch<SetStateAction<boolean>>;
  /** 关闭回调 */
  onClose: () => void;
}

const Choose: ForwardRefRenderFunction<ChooseHandle, ChooseProps> = (
  {
    userImage,
    sortOption,
    pathImage,
    colors,
    selectedColor,
    createPathImage,
    resetOptionLoading,
    setSelectedColor,
    setSelectedOption,
    setTaskStatus,
    setZuploaderVisible,
    onClose,
  },
  ref
) => {
  /** 分类列表内容 DOM */
  const _chooseContent = useRef<HTMLDivElement>(null);
  /** 选项列表内容 DOM */
  const _optionLayout = useRef<HTMLDivElement>(null);
  /** 选项列表 */
  const [optionList, setOptionList] = useState<ImageSort[]>([]);
  /** 选择列表底图宽度 */
  const [bottomImageWidth, setBottomImageWidth] = useState<number>(0);
  /** 强制更新 */
  const [count, setCount] = useState<number>(0);
  /** 已选择的分类 */
  // 默认选择全部
  let selectedName = i18n.format("modules.mini.me.all");
  const sortNames = Object.keys(sortOption);
  // 当选项只有一个的时候，则不选择全部
  if (sortNames.length === 2) {
    selectedName = sortNames.filter((item) => item != selectedName)[0];
  }
  const [selectedSort, setSelectedSort] = useState<string>(selectedName);

  useImperativeHandle(ref, () => ({
    update: () => {
      setOptionList(sortOption[selectedSort].slice(0, 10));
      if (Object.keys(pathImage).length === 0) {
        createPathImage();
      }
      setCount(count + 1);
    },
  }));

  /** 点击关闭 */
  const handleClose = () => {
    onClose();
  };

  /** 选择页面滚动事件 */
  const handleChooseScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if ((scrollHeight - clientHeight) * 0.8 <= scrollTop) {
      const currentOption = sortOption[selectedSort];
      const index = optionList.length;
      let nextIndex = index + 10;
      if (nextIndex > currentOption.length) {
        nextIndex = currentOption.length;
      }
      setOptionList([...optionList, ...currentOption.slice(index, nextIndex)]);
    }
  };

  /** 分类选项点击 */
  const handleSelectOption = (option: string) => {
    _chooseContent.current?.scrollTo(0, 0);
    setSelectedSort(option);
    setOptionList(sortOption[option].slice(0, 10));
  };

  /** 选项点击 */
  const handleOptionClick = (option: ImageSort) => {
    setSelectedOption(option);
    setTaskStatus(TaskStatus.Preview);
  };

  /** 肤色点击 */
  const handleSelectColor = (color: Color) => {
    resetOptionLoading();
    setSelectedColor(color);
  };

  useEffect(() => {
    if (_chooseContent.current) {
      setOptionList(sortOption[selectedSort].slice(0, 10));
      if (Object.keys(pathImage).length === 0) {
        createPathImage();
      }
      if (_optionLayout.current) {
        setBottomImageWidth(
          Math.floor((_optionLayout.current.clientWidth - 48) / 2)
        );
      }
    }
  }, []);

  return (
    <Wrapper className={styles.choose}>
      <Header
        className={classnames(styles.chooseHeader, {
          [styles.hideAfter]: sortNames.length === 2,
        })}
        closeIconColor="#000"
        onClose={handleClose}
        renderContent={
          <div className={styles.sortLayout}>
            {/*  当只有一份分类且颜色也只有一种时不显示分类 */}
            {(sortNames.length > 2 || colors.length > 1) &&
              Object.keys(sortOption).map((item, index) => {
                return (
                  <div key={index} className={styles.sortOptionLayout}>
                    <div
                      className={classnames(styles.sortOption, {
                        [styles.sortSeleted]: selectedSort === item,
                      })}
                      onClick={() => handleSelectOption(item)}
                    >
                      <div className={styles.sortText}>
                        {item.split("-")[0]}
                      </div>
                      <div className={styles.sortNumber}>
                        {sortOption[item].length}
                      </div>
                    </div>
                  </div>
                );
              })}
            {/*  当只有一份分类且颜色也只有一种时显示换图 */}
            {sortNames.length === 2 && colors.length === 1 && (
              <>
                <h1 className={styles.headerTitle}>
                  {i18n.format("modules.mini.me.choose.style")}
                </h1>
                <div
                  className={styles.replace}
                  onClick={() => setZuploaderVisible(true)}
                >
                  <Icon type="photo-avatar" color="#ff533a" size={20} />
                  <span>{i18n.format("modules.global.replace")}</span>
                </div>
              </>
            )}
          </div>
        }
      />
      {colors.length > 1 && (
        <div className={styles.featuresLayout}>
          <div className={styles.chooseColorWrap}>
            <div className={styles.title}>
              {i18n.format("modules.mini.me.choose.skin.color.title")}
            </div>
            <div className={styles.colorOptions}>
              {colors.map((item, index) => (
                <div key={index} className={styles.colorOptionWrap}>
                  <div
                    style={{
                      backgroundColor: item.color,
                    }}
                    className={classnames(styles.colorOption, {
                      [styles.selected]: selectedColor.name === item.name,
                    })}
                    onClick={() => handleSelectColor(item)}
                  >
                    <div className={styles.selectedIcon}>
                      <Icon type="tick-circle" color="#ff533a" size={18} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            className={styles.replace}
            onClick={() => setZuploaderVisible(true)}
          >
            <Icon type="photo-avatar" color="#ff533a" size={20} />
            <span>{i18n.format("modules.global.replace")}</span>
          </div>
        </div>
      )}

      <div
        ref={_chooseContent}
        className={styles.content}
        onScroll={handleChooseScroll}
      >
        <div ref={_optionLayout} className={styles.optionLayout}>
          {optionList.map((item, index) => (
            <ChooseOption
              key={index}
              count={count}
              option={item}
              bottomImageWidth={bottomImageWidth}
              avatar={userImage}
              pathImage={pathImage}
              selectedColor={selectedColor}
              handleOptionClick={handleOptionClick}
            />
          ))}
        </div>
      </div>
    </Wrapper>
  );
};

export default forwardRef(Choose);
